<template>
  <div>
    <FieldWrapper :field="field">
      <div class="d-flex align-items-center">
        <input
          ref="input"
          v-model="localeText"
          type="text"
          class="form-control"
          :placeholder="getSnippet(field.placeholder)"
          :class="[validationClass]"
          :disabled="isDisabled"
        />
        <div class="locale-switch ml-2 d-flex">
          <button
            v-for="locale in locales"
            :key="locale.key"
            type="button"
            class="locale-switch__btn"
            :class="{
              'locale-switch__btn--active': activeLocale === locale.key
            }"
            @click="setLocale(locale.key)"
          >
            <img
              :src="locale.flag"
              :alt="locale.label"
              class="locale-switch__flag"
            />
          </button>
        </div>
      </div>
      <template v-if="field.hint" #hints>
        {{ getSnippet(field.hint) }}
      </template>
    </FieldWrapper>
  </div>
</template>

<script>
import FieldWrapper from "@/components/Tools/FormHelper/Components/FieldWrapper";
import {
  base,
  eagerValidation
} from "@/components/Tools/FormHelper/Helper/mixins";
import {
  parseLocalizedString,
  getLocalizedStringLocales
} from "@/composables/useLocalizedDescription";
import i18n from "@/core/plugins/vue-i18n";

export default {
  components: { FieldWrapper },
  mixins: [base, eagerValidation],
  data() {
    return {
      activeLocale: i18n.locale,
      locales: getLocalizedStringLocales(),
      localeValues: {},
      suppressSync: false
    };
  },
  computed: {
    localeText: {
      get() {
        return this.localeValues[this.activeLocale] ?? "";
      },
      set(text) {
        this.$set(this.localeValues, this.activeLocale, text);
        this.writeBack();
      }
    },
    // Override mixin's `value` so vuelidate validators (required, minLength, ...)
    // run against the longest locale text instead of the raw JSON envelope.
    value: {
      get() {
        let longest = "";
        for (const key of Object.keys(this.localeValues)) {
          const text = this.localeValues[key] ?? "";
          if (text.length > longest.length) longest = text;
        }
        return longest;
      },
      set(val) {
        this.$emit("input", val);
      }
    }
  },
  watch: {
    defaultValue: {
      immediate: true,
      handler(newVal) {
        if (!this.suppressSync) {
          this.syncFromModel(newVal);
        }
      }
    }
  },
  methods: {
    syncFromModel(raw) {
      const parsed = parseLocalizedString(raw);
      const updated = {};
      for (const locale of this.locales) {
        updated[locale.key] = parsed[locale.key] ?? "";
      }
      this.localeValues = updated;
    },
    setLocale(locale) {
      this.activeLocale = locale;
    },
    writeBack() {
      const values = {};
      for (const locale of this.locales) {
        if (this.localeValues[locale.key]) {
          values[locale.key] = this.localeValues[locale.key];
        }
      }
      this.suppressSync = true;
      this.$emit("input", JSON.stringify(values));
      this.$nextTick().then(() => {
        this.suppressSync = false;
      });
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$anyError;
    }
  }
};
</script>

<style lang="scss" scoped>
.locale-switch {
  gap: 2px;
  flex-shrink: 0;

  &__btn {
    display: flex;
    align-items: center;
    padding: 4px 6px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: none;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #f3f3f3;
    }

    &--active {
      background: #f3f3f3;
      border-color: #d0d0d0;
    }
  }

  &__flag {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 3px;
  }
}
</style>
